import React, { useState, useContext, } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  FormControl,
  Grid,
} from '@mui/material';
import { BuilderBlocks } from '@builder.io/react/lite';
import {
  MagicSvgContext,
  MagicSvgDispatchContext
} from '../../../context/MagicSvgContext/MagicSvgContext';

import {
  ButtonStyled,
  InputSection,
  GenerateFormWrapper,
  FormLabelText,
  ToggleButtonStyled,
  ToggleButtonGroupStyled,
  ToggleButtonImage,
  ToggleButtonText,
} from '../magic-svg.styles';

export default function Generate({ artStyles }) {
  const { image, builderPageId, generateButtonContent } = useContext(MagicSvgContext);
  const { onGenerate } = useContext(MagicSvgDispatchContext);

  const [prompt, setPrompt] = useState(image.prompt || '');
  const [additionalFields, setAdditionalFields] = useState(image.additionalFields || {});
  const [artStyle, setArtStyle] = useState(
    image.artStyle?.replace('-text', '')
      || artStyles.filter(style => style.isActive)[0]?.id
  );

  const handlePromptChange = (event) => {
    setPrompt(event.target.value);
  };
  const handleArtStyleChange = (event, value) => {
    // Shouldn't be selectable when disabled, but just in case
    if (value !== null && value !== 'disabled') {
      setArtStyle(value);
    }
  };

  const handleAdditionalFieldsChange = (event) => {
    const { id, value } = event.target;
    setAdditionalFields({ ...additionalFields, [id]: value });
  };

  const handleSubmit = (event) => {
    // TODO: check if user has remaining token
    event.preventDefault();

    const containsUserText = additionalFields.userText && additionalFields.userText.trim();

    onGenerate(prompt, `${artStyle}${containsUserText ? '-text' : ''}`, additionalFields);
  };

  return (
    <GenerateFormWrapper
      component='form'
      noValidate
      onSubmit={handleSubmit}>
      <InputSection container spacing={10}>
        <Grid item md={12} lg={6} sx={{ width: '100%' }}>
          <FormControl sx={{ width: '100%' }}>
            <FormLabelText htmlFor='prompt'>Describe any design you can imagine:</FormLabelText>
            <TextField
              variant='outlined'
              id='prompt'
              multiline
              onChange={handlePromptChange}
              value={prompt}
              helperText='*Up to 300 words'
              minRows={3}
              inputProps={{
                maxLength: 300,
              }}
            />
          </FormControl>
          <FormControl sx={{ width: '100%', mt: '2vw' }}>
            <FormLabelText htmlFor='userText'>
              Add text to your design
              <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{` (optional)`}</span>
            </FormLabelText>
            <TextField
              variant='outlined'
              id='userText'
              helperText={`*Bear with me, I'm still learning to spell well!`}
              onChange={handleAdditionalFieldsChange}
              value={additionalFields.userText}
            />
          </FormControl>
        </Grid>
        <Grid item md={12} lg={6}>
          <FormControl>
            <FormLabelText htmlFor='artStyle'>Choose a Design Style</FormLabelText>
            <ToggleButtonGroupStyled
              id='artStyle'
              exclusive
              value={artStyle}
              aria-label='art style'
              onChange={handleArtStyleChange}>
              {artStyles.length > 0 && artStyles.map(style => (
                <ToggleButtonStyled
                  key={style.id}
                  value={style.isActive ? style.id : 'disabled'}
                  disabled={!style.isActive}
                  disableRipple>
                  <ToggleButtonImage src={style.imageUrl} alt={style.label} />
                  <ToggleButtonText variant='subtitle1'>{style.label}</ToggleButtonText>
                </ToggleButtonStyled>
              ))}
            </ToggleButtonGroupStyled>
          </FormControl>
        </Grid>
      </InputSection>
      <ButtonStyled
        type='submit'
        variant='contained'>
        {builderPageId ? (
          <BuilderBlocks
            fieldName='generateButtonContent'
            child
            parentElementId={builderPageId}
            blocks={generateButtonContent}
          />
        ) : null}
      </ButtonStyled>
    </GenerateFormWrapper>
  );
};

Generate.propTypes = {
  artStyles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      imageUrl: PropTypes.string,
      isActive: PropTypes.bool,
      label: PropTypes.string,
    }),
  ),
};

Generate.defaultProps = {
  artStyles: [],
};
